// src/components/Admin/PaymentsTab.js
import { useState, useEffect, useCallback } from "react";
import { DollarSign, RotateCcw, TrendingUp, CreditCard } from "lucide-react";
import { fetchPaymentsSummary } from "../services/adminPayments";

const METHOD_FILTERS = [
  { id: "all", label: "All" },
  { id: "card", label: "Card" },
  { id: "applepay", label: "Apple Pay" },
  { id: "stcpay", label: "STC Pay" },
  { id: "wallet", label: "Wallet" },
];

const STATUS_STYLES = {
  paid: "bg-green-100 text-green-700",
  pending: "bg-orange-100 text-orange-700",
  failed: "bg-red-100 text-red-700",
  refunded: "bg-gray-100 text-gray-600",
};

function StatusBadge({ status }) {
  const s = status || "pending";
  const style = STATUS_STYLES[s] || "bg-gray-100 text-gray-600";
  return (
    <span className={`text-xs px-2.5 py-0.5 rounded-full capitalize ${style}`}>
      {s}
    </span>
  );
}

function formatDate(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString();
}

const money = (n) => `${(Number(n) || 0).toLocaleString()} SAR`;

function SummaryCard({ icon, label, value, tone = "text-[#1E3A8A]" }) {
  return (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <div className="flex items-center gap-3 mb-2">
        <span className="text-[#006C35]">{icon}</span>
        <span className="text-gray-600">{label}</span>
      </div>
      <div className={`text-3xl ${tone}`}>{value}</div>
    </div>
  );
}

export default function PaymentsTab() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSummary(await fetchPaymentsSummary());
    } catch (err) {
      console.error("fetchPaymentsSummary error:", err);
      setError("Could not load payments.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <div className="text-gray-500 py-8">Loading payments...</div>;
  if (error) return <div className="text-red-600 py-8">{error}</div>;

  const payments = summary.payments || [];
  const shown = payments.filter((p) =>
    filter === "all" ? true : (p.paymentMethod || "") === filter
  );

  return (
    <div>
      {/* Summary cards */}
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <SummaryCard
          icon={<DollarSign size={24} />}
          label="Total Collected"
          value={money(summary.collected)}
        />
        <SummaryCard
          icon={<RotateCcw size={24} />}
          label="Refunded"
          value={money(summary.refunded)}
          tone="text-red-600"
        />
        <SummaryCard
          icon={<TrendingUp size={24} />}
          label="Profit"
          value={money(summary.profit)}
          tone="text-[#006C35]"
        />
      </div>

      {/* Filter */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="text-xl font-bold text-gray-800">Payments</h2>
        <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden flex-wrap">
          {METHOD_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                filter === f.id
                  ? "bg-[#006C35] text-white"
                  : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {shown.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-gray-400 py-16">
          <CreditCard size={40} className="mb-3" />
          <p>No payments here.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Booking</th>
                  <th className="text-left px-4 py-3 font-medium">Fee</th>
                  <th className="text-left px-4 py-3 font-medium">Method</th>
                  <th className="text-left px-4 py-3 font-medium">Source</th>
                  <th className="text-left px-4 py-3 font-medium">Status</th>
                  <th className="text-left px-4 py-3 font-medium">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {shown.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="text-gray-800">{p.bookingId || "—"}</div>
                      <div className="text-xs text-gray-400">
                        {p.moyasarPaymentId || p.id}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {p.platformFee ? money(p.platformFee) : "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-600 capitalize">
                      {p.paymentMethod || "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-600 uppercase text-xs">
                      {p.sourceType || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={p.status} />
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {formatDate(p.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}